import React, { useState, useEffect } from 'react';
import { HiOutlineUpload, HiOutlineDocumentText, HiOutlineScissors, HiOutlineDownload } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import './UploadTab.css';

const UploadTab = ({ canCreate }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [splitFile, setSplitFile] = useState(null);
    const [splitting, setSplitting] = useState(false);
    const [splitProgress, setSplitProgress] = useState(0);

    useEffect(() => {
        fetchUsers();
        fetchTemplates();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/users');
            if (response.data.success) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/templates');
            if (response.data.success) {
                setTemplates(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (file.type !== 'application/pdf') {
                alert('Please select a PDF file');
                e.target.value = '';
                return;
            }
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                e.target.value = '';
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        
        if (!selectedUser) {
            alert('Please select an email user');
            return;
        }

        if (!selectedFile) {
            alert('Please select a PDF file');
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('user_id', selectedUser);
            if (selectedTemplate) {
                formData.append('template_id', selectedTemplate);
            }

            const response = await axiosInstance.post('/api/aging-reports/upload/pdf', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            });

            if (response.data.success) {
                alert('PDF uploaded and emails sent successfully!');
                setSelectedFile(null);
                setSelectedUser('');
                setSelectedTemplate('');
                document.getElementById('file-input').value = '';
            }
        } catch (error) {
            console.error('Error uploading PDF:', error);
            const check = error.response?.data?.error?.detail?.smtp_check;
            alert(check
                ? `${error.response.data.message}\n\n${check.hint}\n\nServer reply: ${check.raw}\n\nUse "Test connection" in Email Users after fixing it.`
                : error.response?.data?.message || 'Failed to upload PDF');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleSplitFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                alert('Please select a PDF file');
                e.target.value = '';
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                e.target.value = '';
                return;
            }
            setSplitFile(file);
        }
    };

    const handleSplit = async (e) => {
        e.preventDefault();

        if (!splitFile) {
            alert('Please select a PDF file');
            return;
        }

        try {
            setSplitting(true);
            setSplitProgress(0);

            const formData = new FormData();
            formData.append('file', splitFile);

            const response = await axiosInstance.post('/api/aging-reports/split-pdf', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                responseType: 'blob',
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setSplitProgress(progress);
                }
            });

            // Pull the server-suggested filename out of Content-Disposition, if present
            const disposition = response.headers['content-disposition'];
            let downloadName = `split_${splitFile.name.replace(/\.pdf$/i, '')}.zip`;
            const match = disposition && disposition.match(/filename="?([^"]+)"?/);
            if (match) {
                downloadName = match[1];
            }

            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            setSplitFile(null);
            document.getElementById('split-file-input').value = '';
        } catch (error) {
            console.error('Error splitting PDF:', error);
            // Error responses come back as a Blob (JSON) because of responseType: 'blob'
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const parsed = JSON.parse(text);
                    alert(parsed.message || parsed.detail || 'Failed to split PDF');
                } catch (parseErr) {
                    alert('Failed to split PDF');
                }
            } else {
                alert(error.response?.data?.message || 'Failed to split PDF');
            }
        } finally {
            setSplitting(false);
            setSplitProgress(0);
        }
    };

    return (
        <div className="upload-tab">
            <div className="upload-header">
                <h2>Upload Aging Report PDF</h2>
                <p>Upload a PDF file to automatically split and send to customers</p>
            </div>

            <div className="upload-container">
                <form onSubmit={handleUpload} className="upload-form">
                    <div className="form-group">
                        <label>Select Email User *</label>
                        <select
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            required
                            disabled={uploading}
                        >
                            <option value="">-- Select User --</option>
                            {users.filter(u => u.is_active).map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                        <small>This user's email credentials will be used to send reports</small>
                    </div>

                    <div className="form-group">
                        <label>Email Template</label>
                        <select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={uploading}
                        >
                            <option value="">-- Use Default Template --</option>
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.name}{template.is_default ? ' (Default)' : ''}
                                </option>
                            ))}
                        </select>
                        <small>Manage and preview templates in the Templates tab</small>
                    </div>

                    <div className="form-group">
                        <label>Select PDF File *</label>
                        <div className="file-input-wrapper">
                            <input
                                id="file-input"
                                type="file"
                                accept=".pdf"
                                onChange={handleFileSelect}
                                required
                                disabled={uploading}
                            />
                            {selectedFile && (
                                <div className="file-info">
                                    <HiOutlineDocumentText size={20} />
                                    <span>{selectedFile.name}</span>
                                    <span className="file-size">
                                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </span>
                                </div>
                            )}
                        </div>
                        <small>PDF will be split by ledger code and sent to respective customers</small>
                    </div>

                    {uploading && (
                        <div className="upload-progress">
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <span>{uploadProgress}% uploaded</span>
                        </div>
                    )}

                    {canCreate && (
                        <button 
                            type="submit" 
                            className="btn-upload" 
                            disabled={uploading || !selectedUser || !selectedFile}
                        >
                            <HiOutlineUpload size={20} />
                            {uploading ? 'Uploading...' : 'Upload & Send Emails'}
                        </button>
                    )}
                </form>

                <div className="upload-info">
                    <h3>How it works:</h3>
                    <ol>
                        <li>Select the email user whose credentials will be used for sending</li>
                        <li>Upload a PDF containing aging reports for multiple customers</li>
                        <li>The system will automatically detect ledger codes in the PDF</li>
                        <li>PDF will be split into separate files for each customer</li>
                        <li>Emails will be sent to each customer with their respective report</li>
                    </ol>
                    
                    <div className="info-box">
                        <strong>Note:</strong> Make sure the PDF contains ledger codes that match
                        your customer database. Pages without ledger codes will be attached to the
                        previous customer's report.
                    </div>
                </div>
            </div>

            <div className="split-section">
                <div className="split-header">
                    <h2>Split PDF Only</h2>
                    <p>Split a bulk PDF by ledger code and download it as a ZIP — no emails sent, nothing saved</p>
                </div>

                <div className="upload-container">
                    <form onSubmit={handleSplit} className="upload-form split-form">
                        <div className="form-group">
                            <label>Select PDF File *</label>
                            <div className="file-input-wrapper">
                                <input
                                    id="split-file-input"
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleSplitFileSelect}
                                    required
                                    disabled={splitting}
                                />
                                {splitFile && (
                                    <div className="file-info">
                                        <HiOutlineDocumentText size={20} />
                                        <span>{splitFile.name}</span>
                                        <span className="file-size">
                                            ({(splitFile.size / 1024 / 1024).toFixed(2)} MB)
                                        </span>
                                    </div>
                                )}
                            </div>
                            <small>The ZIP will contain one PDF per customer, downloaded automatically</small>
                        </div>

                        {splitting && (
                            <div className="upload-progress">
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill split-progress-fill"
                                        style={{ width: `${splitProgress}%` }}
                                    />
                                </div>
                                <span>{splitProgress}% uploaded</span>
                            </div>
                        )}

                        {canCreate && (
                            <button
                                type="submit"
                                className="btn-upload btn-split"
                                disabled={splitting || !splitFile}
                            >
                                {splitting ? <HiOutlineScissors size={20} /> : <HiOutlineDownload size={20} />}
                                {splitting ? 'Splitting...' : 'Split & Download ZIP'}
                            </button>
                        )}
                    </form>

                    <div className="upload-info">
                        <h3>How it works:</h3>
                        <ol>
                            <li>Upload a PDF containing aging reports for multiple customers</li>
                            <li>The system scans each page for a ledger code</li>
                            <li>Pages are grouped into one PDF per customer</li>
                            <li>All customer PDFs are zipped and downloaded to your browser</li>
                        </ol>

                        <div className="info-box split-info-box">
                            <strong>Note:</strong> This only splits the file — it does not send emails or
                            write anything to the database. Use "Upload &amp; Send Emails" above if you
                            want the reports emailed to customers.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadTab;
